import { createFileRoute } from "@tanstack/react-router";
import { ApiInternal } from "src/api/api_internal";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsGripHorizontal } from "react-icons/bs";
import { DialogActions } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { PrimaryButton } from "src/components/base/button";
import { Textarea } from "src/components/base/textarea";
import CHANGELOG_CONTEXT from "src/context/changelog";
import { Badge } from "src/components/base/badge";
import DEVICE_CONTEXT from "src/context/internal/device";
import { notify } from "src/components/base/toast";

const MIN_HEIGHT = 60;
const INITIAL_HEIGHT = 480;
const PLACEHOLDER = "Device state override";

/**
 * The properties for {@link DebugDeviceView}
 */
export type DebugDeviceViewProps = {};

/**
 * Debug view for a device that allows overriding the device state.
 *
 * @returns The rendered view.
 */
function DebugDeviceView(props: DebugDeviceViewProps): React.ReactElement {
    const [t] = useTranslation("admin-device-view");

    const { isProd } = React.useContext(CHANGELOG_CONTEXT);
    const { device } = React.useContext(DEVICE_CONTEXT);

    const dumpedState = Route.useLoaderData();
    const [value, setValue] = useState("");
    const [height, setHeight] = useState(INITIAL_HEIGHT);

    const isValidJson =
        !value ||
        (() => {
            try {
                JSON.parse(value);
                return true;
            } catch {
                return false;
            }
        })();
    const dragState = useRef<{ startY: number; startH: number } | null>(null);

    const onPointerDown = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            dragState.current = { startY: e.clientY, startH: height };
            e.currentTarget.setPointerCapture(e.pointerId);
            e.preventDefault();
        },
        [height],
    );

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragState.current) return;
        const delta = e.clientY - dragState.current.startY;
        setHeight(Math.max(MIN_HEIGHT, dragState.current.startH + delta));
    }, []);

    const onPointerUp = useCallback(() => {
        dragState.current = null;
    }, []);

    /**
     * Override the device's Tailscale state.
     */
    const debugOverrideDeviceState = async () => {
        await ApiInternal.devices.setDeviceStateDebugOnly(device.uuid, JSON.parse(value));
        notify.success(t("toast.success"));
    };

    return isProd ? (
        <Badge color="red">{t("description.debug-only")}</Badge>
    ) : (
        <Form className={"flex h-full flex-col"} onSubmit={debugOverrideDeviceState}>
            <div className={"flex flex-col gap-1"}>
                <div className={"flex gap-1"} style={{ height }}>
                    <Textarea
                        className={"flex-1"}
                        placeholder={PLACEHOLDER}
                        resizable={false}
                        required={true}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        data-invalid={value && !isValidJson ? true : undefined}
                    />
                    <Textarea
                        className={"flex-1"}
                        resizable={false}
                        disabled={true}
                        value={JSON.stringify(dumpedState, null, 2)}
                    />
                </div>
                <div
                    className={
                        "flex w-full cursor-ns-resize touch-none items-center justify-center rounded bg-zinc-100 py-0.5 dark:bg-zinc-800"
                    }
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                >
                    <BsGripHorizontal className={"text-zinc-400 dark:text-zinc-500"} />
                </div>
            </div>
            <DialogActions>
                <PrimaryButton type={"submit"} disabled={!value || !isValidJson}>
                    {t("button.apply-override")}
                </PrimaryButton>
            </DialogActions>
        </Form>
    );
}

export const Route = createFileRoute("/_admin/a/devices/$deviceId/_device/debug")({
    component: DebugDeviceView,
    loader: async ({ params }) => await ApiInternal.devices.dumpDeviceState(params.deviceId),
});
